import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Check, Loader2, RefreshCw } from 'lucide-react';
import AccountLayout from '../components/AccountLayout';
import { notificationsApi, type InAppNotificationItem, type InAppFeedResult } from '../services/notificationsApi';

export default function NotificationsPage() {
  const { t } = useTranslation();

  const [feedLoading, setFeedLoading] = useState(false);
  const [feed, setFeed] = useState<InAppFeedResult | null>(null);

  const loadFeed = async () => {
    setFeedLoading(true);
    try {
      const data = await notificationsApi.inboxFeed({ limit: 50 });
      setFeed(data);
    } catch (e) {
      console.error('Failed to load inbox feed', e);
    } finally {
      setFeedLoading(false);
    }
  };

  useEffect(() => {
    void loadFeed();
  }, []);

  const handleMarkRead = async (id: string) => {
    try {
      await notificationsApi.mark(id, 'read');
      await loadFeed();
    } catch (e) {
      console.error('Failed to mark notification', e);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAll('read');
      await loadFeed();
    } catch (e) {
      console.error('Failed to mark all', e);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <AccountLayout>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold text-textPrimary mb-2">
            {t('auth.account.notifications.title')}
          </h1>
          <p className="text-textSecondary">
            {t('auth.account.notifications.subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-50 rounded-lg">
                <Bell className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textPrimary">
                  {t('auth.account.sidebar.notifications')}
                </h2>
                <p className="text-sm text-textSecondary">
                  {feed ? `${feed.unreadCount} non lue(s)` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => void loadFeed()}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
                <span>{t('common.refresh', { defaultValue: 'Rafraîchir' })}</span>
              </button>
              <button
                onClick={() => void handleMarkAllRead()}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                {t('auth.account.notifications.markAllRead', { defaultValue: 'Tout marquer comme lu' })}
              </button>
            </div>
          </div>

          {feedLoading ? (
            <div className="flex items-center gap-2 text-textSecondary py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{t('common.loading', { defaultValue: 'Chargement...' })}</span>
            </div>
          ) : (
            <div className="space-y-3">
              {(feed?.data ?? []).map((item: InAppNotificationItem) => (
                <div
                  key={item.id}
                  className={`p-4 rounded-lg border transition-colors ${
                    item.read
                      ? 'border-gray-200 bg-white'
                      : 'border-primary-200 bg-primary-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-textPrimary truncate">
                          {item.title}
                        </p>
                        {!item.read && (
                          <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full flex-shrink-0">
                            {t('auth.account.notifications.new', { defaultValue: 'Nouveau' })}
                          </span>
                        )}
                      </div>
                      {item.body && (
                        <p className="text-sm text-textSecondary mt-1 whitespace-pre-wrap">
                          {item.body}
                        </p>
                      )}
                      <p className="text-xs text-textSecondary mt-2">
                        {formatDate(item.createdAt)}
                      </p>
                    </div>
                    {!item.read && (
                      <button
                        onClick={() => void handleMarkRead(item.id)}
                        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>{t('auth.account.notifications.markRead', { defaultValue: 'Lu' })}</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {(feed?.data ?? []).length === 0 && (
                <div className="text-center py-12 text-textSecondary">
                  <Bell className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">
                    {t('auth.account.notifications.empty', { defaultValue: 'Aucune notification pour le moment.' })}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AccountLayout>
  );
}
